import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import {
  MdDelete,
  MdPerson,
  MdPersonAddAlt1,
  MdThumbDown,
} from "react-icons/md";
import { FILE_STORAGE_SERVER_URL, ICON_SIZES } from "src/helpers/constants";
import { motion } from "framer-motion";
import { memo, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCurrentUser from "src/hooks/useCurrentUser";
import { formatLongDate, getShortName } from "src/helpers/utils";
import useMobile from "src/hooks/useMobile";
import { Tooltip } from "react-tooltip";
import useUsers from "src/hooks/useUsers";
import NetworkProfile from "../NetworkProfile/NetworkProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import useCache from "src/hooks/useCache";
import { EButtonKind } from "../Button/ButtonTypes";
import BlockContactModal from "../BlockContactModal/BlockContactModal";

const MOBILE_SIZE = 58;
const DESKTOP_SIZE = 64;

interface IReceivedContactInviteCard {
  user: string;
  index: number;
  date: string;
}

const ReceivedContactInviteCard: React.FC<IReceivedContactInviteCard> = ({
  user,
  index,
  date,
}) => {
  const { isMobile } = useMobile();
  const { clearCache } = useCache();
  const { getUserById } = useUsers();
  const sourceTooltipId = useId();
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showRejectDialog, setShowRejectDialog] = useState<boolean>(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState<boolean>(false);
  const [showBlockDialog, setShowBlockDialog] = useState<boolean>(false);

  const navigate = useNavigate();
  const { getAccountById } = useCurrentUser();

  const userInfo = useMemo(() => {
    return getUserById({ _id: user });
  }, [user]);

  return (
    <>
      <motion.div
        className="flex w-full"
        initial={{
          opacity: 0,
          x: index % 2 === 0 ? -100 : 100,
        }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
          delay: index * 0.1,
        }}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "bg-sky-200/60 dark:bg-gray-900/50",
            "p-3 md:p-3",
            "rounded-md",
            "md:gap-4",
            "gap-2",
            "cursor-pointer",
          ])}
          onClick={(e: any) => {
            e?.preventDefault?.();
            setShowUserInfoModal(true);
          }}
        >
          <div className={clsx(["flex", "flex-row", "gap-2"])}>
            <div
              className={clsx([
                `!min-w-[${MOBILE_SIZE}px]`,
                `!min-h-[${MOBILE_SIZE}px]`,
                `!max-w-[${MOBILE_SIZE}px]`,
                `!max-h-[${MOBILE_SIZE}px]`,
                `md:!min-w-[${DESKTOP_SIZE}px]`,
                `md:!min-h-[${DESKTOP_SIZE}px]`,
                `md:!max-w-[${DESKTOP_SIZE}px]`,
                `md:!max-h-[${DESKTOP_SIZE}px]`,
                "rounded-md",
                "overflow-hidden",
                "inline-flex",
                "items-center",
                "justify-center",
                "cursor-pointer",
                "relative",
              ])}
              data-tooltip-id={sourceTooltipId}
              data-tooltip-content={isMobile ? "" : "Ver perfil"}
              data-tooltip-position-strategy="fixed"
              data-tooltip-open-on="hover"
              data-tooltip-offset={0}
            >
              {userInfo?.picture ? (
                <img
                  src={`${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`}
                  className={clsx(["bg-checkers", "!w-full", "!h-full"])}
                  alt={userInfo?.name}
                />
              ) : (
                <MdPerson size={ICON_SIZES.XL} />
              )}
              {!isMobile && (
                <Tooltip
                  id={sourceTooltipId}
                  style={{ zIndex: 99 }}
                  positionStrategy="fixed"
                />
              )}
            </div>
            <div className={clsx(["flex", "flex-col", "w-full"])}>
              <div
                className={clsx([
                  "text-sm",
                  "md:text-base",
                  "font-normal",
                  "truncate",
                ])}
              >
                {userInfo?.name}
              </div>
              <div className={clsx(["text-sm", "font-medium", "truncate"])}>
                {formatLongDate(date)}
              </div>
              <div className={clsx(["text-xs"])}>
                <div
                  className={clsx([
                    "relative",
                    "inline-flex",
                    "after-animated-underline after:bg-current",
                  ])}
                  onClick={(e: any) => {
                    e?.stopPropagation?.();
                    setShowBlockDialog(true);
                  }}
                >
                  Bloquear
                </div>
              </div>
            </div>
          </div>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-row",
              "gap-6",
              "justify-between",
              "items-center",
            ])}
          >
            <div
              className={clsx([
                "flex",
                "flex-row",
                "items-center",
                "text-xl",
                "md:text-2xl",
                "font-medium",
                "truncate",
                "dark:text-purple-400",
                "text-blue-400",
              ])}
            ></div>
            <ButtonGroup
              buttons={[
                {
                  tooltip: "Rechazar",
                  icon: <MdThumbDown size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();
                    setShowRejectDialog(true);
                  },
                  kind: EButtonKind.DANGER,
                },
                {
                  tooltip: "Aceptar",
                  icon: <MdPersonAddAlt1 size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();
                    setShowAcceptDialog(true);
                  },
                  kind: EButtonKind.SAVE,
                },
              ]}
              align="right"
            />
          </div>
        </div>
      </motion.div>

      {showUserInfoModal && (
        <NetworkProfile
          profileInfo={userInfo}
          title={
            userInfo?.name
              ? `Acerca de ${getShortName(userInfo?.name)}`
              : "Perfil"
          }
          onClose={() => setShowUserInfoModal(false)}
          actions={[]}
        />
      )}

      {showRejectDialog && (
        <ConfirmDialog
          description={`¿Deseas rechazar la solicitud de contacto de ${getShortName(
            userInfo?.name
          )}?`}
          title="Rechazar"
          onClose={() => setShowRejectDialog(false)}
          onConfirm={() => {}}
          confirmKind={EButtonKind.DANGER}
          isLoading={false}
          doubleConfirmRequired
          note="No pasa nada. Podrán ponerse en contacto después."
        />
      )}

      {showAcceptDialog && (
        <ConfirmDialog
          description={`¿Deseas agregar a ${getShortName(
            userInfo?.name
          )} a tus contactos?`}
          onConfirm={() => {}}
          onClose={() => setShowAcceptDialog(false)}
          confirmKind={EButtonKind.SAVE}
          title="Aceptar"
          isLoading={false}
        />
      )}

      {showBlockDialog && (
        <BlockContactModal onClose={() => setShowBlockDialog(false)} />
      )}
    </>
  );
};

export default memo(ReceivedContactInviteCard);
